import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import PrivacyPolicy from './components/PrivacyPolicy';

const { FiPhone, FiShield, FiTrendingUp, FiClock, FiUsers, FiCheckCircle, FiPlay, FiDollarSign } = FiIcons;

function ScrollToAnchor() {
  const location = useLocation();
  
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);
  
  return null;
}

function MainContent() {
  const [consentChecked, setConsentChecked] = useState(false);
  
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = "https://tally.so/widgets/embed.js";
      script.async = true;
      script.onload = () => {
        if (typeof window.Tally !== 'undefined') {
          window.Tally.loadEmbeds();
        }
      };
      document.body.appendChild(script);
    } else if (typeof window.Tally !== 'undefined') {
      window.Tally.loadEmbeds();
    }
    
    return () => {
      if (existingScript && existingScript.parentNode === document.body) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handleAuditClick = (e) => {
    e.preventDefault();
    const auditSection = document.getElementById('audit');
    if (auditSection) {
      auditSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="pt-20 pb-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Transform Your Dental Practice with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                AI Voice Technology
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              Our intelligent voice agent handles appointment scheduling and insurance verification 24/7, allowing your staff to focus on patient care while increasing practice efficiency.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={handleAuditClick}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Get Your Free ROI Audit
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold text-slate-900 mb-4"
            >
              See Our AI Voice Agent in Action
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-slate-600 max-w-2xl mx-auto"
            >
              Watch (~3 min) how our intelligent voice agent handles appointment scheduling and insurance verification for dental practices.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.youtube.com/embed/PjReYXZPWgU"
                  title="DentalAI Voice Agent Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full rounded-lg"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ROI Audit Section - Moved up */}
      <section id="audit" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Get Your Free ROI Audit
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Discover how much revenue your dental practice could gain with our AI voice agent. Our comprehensive audit analyzes your current operations and provides personalized recommendations.
              </p>
              <div className="space-y-4">
                {[
                  "Analyze your current appointment scheduling efficiency",
                  "Calculate potential revenue increase",
                  "Identify insurance verification bottlenecks",
                  "Receive personalized implementation roadmap"
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                    <span className="text-slate-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Free ROI Audit Form</h3>
                  <p className="text-slate-600">Complete this form to receive your personalized audit</p>
                </div>
                {/* Tally Form */}
                <div className="rounded-lg overflow-hidden">
                  <iframe
                    data-tally-src="https://tally.so/embed/nr9NBl?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
                    loading="lazy"
                    width="100%"
                    height="477"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    title="Dental Practice AI ROI Audit"
                  >
                  </iframe>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Moved down */}
      <section id="features" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-slate-900 mb-4"
            >
              Why Dental Practices Choose Our AI
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-600 max-w-2xl mx-auto"
            >
              Streamline operations, reduce costs, and improve patient satisfaction with our intelligent voice assistant.
            </motion.p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiClock,
                title: "24/7 Availability",
                description: "Never miss an appointment request. Our AI works around the clock to capture every opportunity."
              },
              {
                icon: FiShield,
                title: "Insurance Verification",
                description: "Automatically verify patient insurance coverage before appointments, reducing claim denials."
              },
              {
                icon: FiDollarSign,
                title: "Financial Optimization",
                description: "Boost revenue while reducing operational costs through efficient scheduling and staff allocation."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-gradient-to-br from-white to-slate-50 p-8 rounded-xl border border-slate-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Contact Us
            </h2>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Call Sarah, our AI to learn more and book an appointment
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-xl border border-slate-200 max-w-md mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiPhone} className="text-white text-2xl" />
                </div>
              </div>
              <a
                href="tel:617-663-0642"
                className="text-3xl font-bold text-blue-600 hover:text-blue-700 transition-colors duration-300 block mb-8"
              >
                617-663-0642
              </a>
              {/* Consent Checkbox */}
              <div className="text-left">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    I agree to receive marketing messaging and appointment reminders from Fish Innovation Group from the phone number provided above. I understand that I will receive 2 messages a month for appointment booking, and response messages if used to communicate with us, data rates may apply, reply STOP to opt out.
                  </span>
                </label>
                {/* Privacy Policy Link */}
                <div className="mt-4 text-center">
                  <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline text-sm">
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToAnchor />
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;